import React, { useEffect, useState } from "react";
import { getStats } from "../../helpers/API/stats";
import { langFlags } from "../../common/langFlags";
import RefreshButton from "../../components/Common/RefreshButton";
import { formatNumber } from "../../utils/numbers";

const Stats = () => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [useOptimized, setUseOptimized] = useState(true);

  // Helper function to safely handle numeric operations
  const safeNumber = (value, defaultValue = 0) => {
    return (value !== null && value !== undefined && !isNaN(value)) ? Number(value) : defaultValue;
  };

  // Helper function to safely format numbers with toFixed
  const safeFixed = (value, decimals = 2, defaultValue = '0.00') => {
    const num = safeNumber(value);
    return num === 0 && value === null ? defaultValue : num.toFixed(decimals);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = await getStats(useOptimized);
      setStats(data);
      console.log('📊 Stats loaded:', data.isOptimized ? 'Optimized version' : 'Full version');
    } catch (error) {
      console.error('❌ Error loading stats:', error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [useOptimized]);

  if (!stats) {
    return (
      <div className="page-content stats">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <div className="spinner-border text-primary"></div>
        </div>
      </div>
    );
  }

  // Safety check for corrupted or incomplete stats
  if (typeof stats !== 'object') {
    return (
      <div className="page-content stats">
        <div className="alert alert-danger">
          <h4>Stats Error</h4>
          <p>Unable to load statistics. Please try refreshing the page.</p>
          <RefreshButton isLoading={isLoading} onRefresh={fetchData} />
        </div>
      </div>
    );
  }

  const statsGroups = {
    group1: [
      {
        label: "Users",
        value: formatNumber(safeNumber(stats.totalUsers)),
        icon: "bx bx-user",
        color: "green",
      },
      {
        label: "Male users",
        value: formatNumber((stats.maleUsers || []).length),
        ratio: safeNumber(stats.maleRatio),
        icon: "bx bx-male",
        extraClass: "highlighted blue",
      },
      {
        label: "Female users",
        value: formatNumber((stats.femaleUsers || []).length),
        ratio: safeNumber(stats.femaleRatio),
        icon: "bx bx-male",
        extraClass: "highlighted pink",
      },
      {
        label: "New Users Last 24 Hours",
        value: formatNumber(safeNumber(stats.newUsersLast24Hours)),
        ratio: safeFixed(stats.ratioNewUsersLast24Hours),
        icon: "bx bx-stats",
        extraClass: "highlighted green",
      },
      {
        label: "New Users Last 10 Days",
        value: formatNumber(safeNumber(stats.newUsersLast10Days)),
        ratio: safeFixed(stats.ratioNewUsersLast10Days),
        icon: "bx bx-stats",
      },
      {
        label: "New Users Last 30 Days",
        value: formatNumber(safeNumber(stats.newUsersLast30Days)),
        ratio: safeFixed(stats.ratioNewUsersLast30Days),
        icon: "bx bx-stats",
      },
      {
        label: "Current Reality",
        value: formatNumber(safeNumber(stats.totalUsersWithCurrentReality)),
        ratio: safeFixed(stats.ratioUsersWithRealityCreated),
        icon: "bx bx-user-circle",
        extraClass: "highlighted orange",
      },
      {
        label: "Current reality 30 D",
        value: formatNumber(safeNumber(stats.totlalUsersWithRealityLast30Days)),
        ratio: safeFixed(stats.ratioTotalUsersWithRealityLast30Days),
        icon: "bx bx-stats",
      },
      {
        label: "Free Demo Completed",
        value: formatNumber(safeNumber(stats.totalUsersWithFreeDemoCompleted)),
        ratio: safeFixed(stats.ratioUsersWithFreeDemoCompleted),
        icon: "bx bx-stats",
        extraClass: "highlighted orange",
      },
      {
        label: "Free Demo Completed Last 10 Days",
        value: formatNumber(safeNumber(stats.totalUsersWithFreeDemoCompletedLast10Days)),
        ratio: safeFixed(stats.ratioUsersWithFreeDemoCompletedLast10Days),
        icon: "bx bx-stats",
        color: "green",
        extraClass: "highlighted green",
      },
      {
        label: "Free demo completed 30 D",
        value: formatNumber(safeNumber(stats.totalUsersWithFreeDemoCompletedLast30Days)),
        ratio: safeFixed(stats.ratioTotalUsersWithFreeDemoCompletedLast30Days),
        icon: "bx bx-stats",
      },
      {
        label: "Ratio of demo completed purchasers",
        value: formatNumber(safeNumber(stats.purchasersWithFreeDemoCompleted)),
        ratio: safeFixed(stats.ratioFreeDemoCompletedPurchasers),
        icon: "bx bx-globe",
      },
      {
        label: "Total users withs Ads Consented",
        value: formatNumber(safeNumber(stats.totalUsersWithAds)),
        ratio: safeFixed(stats.ratioTotalUsersWithAds),
        icon: "bx bx-user-check",
        extraClass: "highlighted orange",
      },
      {
        label: "Total Ads Seen",
        value: `${formatNumber(safeNumber(stats.totalAdsSeen))} - $${safeFixed(safeNumber(stats.totalAdsSeen) * 0.011)}`,
        icon: "bx bx-ad",
        extraClass: "highlighted green",
      },
      {
        label: "Ads per user",
        value: `${formatNumber(safeNumber(stats.avgAdsSeenPerUser))} - $${safeFixed(safeNumber(stats.avgAdsSeenPerUser) * 0.011)}`,
        // ratio: stats.avgProfitPerUser,
        icon: "bx bx-ad",
      },
      {
        label: "Marketing consented",
        value: formatNumber(safeNumber(stats.totalUsersWithMarketingConsented)),
        ratio: safeFixed(stats.ratioTotalUsersWithMarketingConsented),
        icon: "bx bx-user-check",
        extraClass: "highlighted pink",
      },
      {
        label: "Total referred users",
        value: formatNumber(safeNumber(stats.totalReferrals)),
        // ratio: stats.totalReferrals,
        icon: "bx bx-user-plus",
      }
    ],
    group2: [
      {
        label: "Purchasers",
        value: formatNumber(safeNumber(stats.totalPurchasers)),
        ratio: safeFixed(stats.ratioPurchasers),
        icon: "bx bx-stats",
        color: "green",
      },
      {
        label: "Total Purchases",
        value: formatNumber(safeNumber(stats.totalPurchases)),
        ratio: safeFixed(stats.ratioPurchasesByPurchasers),
        icon: "bx bx-cart",
      },
      {
        label: "Female purchasers",
        value: formatNumber(safeNumber(stats.femalePurchasers)),
        ratio: safeFixed(stats.ratioFemalePurchase),
        icon: "bx bx-cart",
        extraClass: "highlighted pink",
      },
      {
        label: "Male purchasers",
        value: formatNumber(safeNumber(stats.malePurchasers)),
        ratio: safeFixed(stats.ratioMalePurchase),
        icon: "bx bx-cart",
        extraClass: "highlighted blue",
      },
      {
        label: "Non-Purchasers",
        value: formatNumber(safeNumber(stats.totalNonPurchasers)),
        ratio: safeFixed(stats.ratioNonPurchasers),
        icon: "bx bx-user-x",
      },
      {
        label: "Purchasers Of 3 Realities",
        value: formatNumber(safeNumber(stats.totalPurchasersOf3Realities)),
        ratio: safeFixed(stats.ratioPurchaseOf3Realities),
        icon: "bx bx-cart-alt",
      },
      {
        label: "Realities Acquired",
        value: formatNumber(safeNumber(stats.totalRealitiesAcquired)),
        ratio: safeFixed(stats.ratioOfRealitiesAcquired),
        icon: "bx bx-globe",
      },
      {
        label: "Purchases last 24 H.",
        value: formatNumber(safeNumber(stats.purchasesLast24Hours)),
        ratio: `$${safeFixed(stats.amountPurchasesLast24Hours)}`,
        icon: "bx bx-stats",
        extraClass: "highlighted green",
      },
      {
        label: "Purchases last 10 D.",
        value: formatNumber(safeNumber(stats.purchasesLast10Days)),
        ratio: `$${safeFixed(stats.amountPurchasesLast10Days)}`,
        icon: "bx bx-stats",
        color: "green",
      },
      {
        label: "Purchases last 30 D.",
        value: formatNumber(safeNumber(stats.purchasesLast30Days)),
        ratio: `$${formatNumber(safeNumber(stats.amountPurchasesLast30Days))}`,
        icon: "bx bx-stats",
      },
      {
        label: "Average Price",
        value: safeFixed(stats.averagePrice),
        currency: "$",
        icon: "bx bx-dollar-circle",
        color: "green",
      },
      {
        label: "Total Income",
        value: formatNumber(safeNumber(stats.totalCost)),
        currency: "$",
        icon: "bx bx-money",
        color: "green",
        extraClass: "highlighted orange",
      },
      {
        label: "Expected purchases next 10 days",
        value: formatNumber(safeNumber(stats.expectedPurchasesNext10Days)),
        ratio: `$${formatNumber(safeNumber(stats.expectedIncomeNext10Days))}`,
        icon: "bx bx-stats",
        color: "green",
      },
      {
        label: "Expected purchases monthly",
        value: formatNumber(safeNumber(stats.expectedPurchasesMonthly)),
        ratio: `$${formatNumber(safeNumber(stats.expectedIncomeMonthly))}`,
        icon: "bx bx-stats",
        color: "green",
        extraClass: "highlighted green",
      },
      {
        label: "Avg. time sign up > purchase",
        value: `${safeFixed(stats.avgTimeFromSignupToPurchase)} h. - ${safeFixed(safeNumber(stats.avgTimeFromSignupToPurchase) / 24)} days`,
        icon: "bx bx-time",
      },
    ],
    group3: (stats.ratioPerLanguage || []).map(item => ({
      label: `<img src="/path/to/flags/${item.language}.png" alt="${item.language} flag"> ${item.language}`,
      value: formatNumber(safeNumber(item.users)),
      ratio: safeFixed(item.ratio),
      language: item.language
    })),
  };


  return (
    <div className="page-content stats">
      {/* Optimization Control Panel */}
      <div className="row mb-3">
        <div className="col-12">
          <div className={`alert ${stats.isOptimized ? 'alert-success' : 'alert-warning'} d-flex justify-content-between align-items-center`}>
            <div>
              <strong>📊 Stats Mode:</strong>
              {stats.isOptimized ? (
                <>
                  Using optimized sampling ({stats.optimizationNote})
                  <br />
                  <small className="text-muted">Estimated total: {formatNumber(stats.estimatedTotal)} users (scale factor: {stats.scaleFactor}x)</small>
                </>
              ) : (
                <>Using full data calculation (may hit quotas)</>
              )}
            </div>
            <div>
              <button
                className={`btn btn-sm ${useOptimized ? 'btn-success' : 'btn-outline-success'} me-2`}
                onClick={() => setUseOptimized(true)}
                disabled={isLoading}
              >
                🚀 Optimized
              </button>
              <button
                className={`btn btn-sm ${!useOptimized ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => setUseOptimized(false)}
                disabled={isLoading}
              >
                🐌 Full Data
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-container">
        <RefreshButton onClick={fetchData} extraClass="refresh" isLoading={isLoading} />
        {Object.entries(statsGroups).map(([groupName, group], index) => (
          <div key={index} className="stats-block">
            {isLoading && (
              <div className="loading">
                <div className="spinner"></div>
              </div>
            )}
            {groupName === "group3" && (
              <div className="stats-item header">
                <label className="stats-label bold">Lang</label>
                <p className="stats-value bold">Users</p>
                <p className="stats-value bold">Purchases</p>
              </div>
            )}
            {group.map((item, index) => (
              <div
                key={index}
                className={`
                  stats-item ${item.extraClass || ""} ${index === 0 ? "highlighted green" : ""}
                `}>
                <label className="stats-label">
                  {groupName === "group3" ? (
                    <>
                      <img
                        src={langFlags[item.language]}
                        alt={`${item.language} flag`}
                        style={{
                          width: "20px",
                          height: "20px",
                          marginRight: "5px"
                        }} />
                      <span>{item.language}</span>
                    </>
                  ) : (
                    item.label
                  )}
                  {item.icon && <i className={item.icon} style={{ marginLeft: "5px" }}></i>}
                </label>
                <p className={`stats-value stats-value-${item.color}`}>
                  {item.currency && <span className="stats-currency">{item.currency}</span>}
                  {item.value}
                  {item.ratio && <span className="stats-ratio">({item.ratio})</span>}
                </p>
                {groupName === "group3" && (
                  <p className={`stats-value stats-value-${item.color}`}>
                    {safeNumber(stats.purchasesPerLanguage?.[item.language]) > 0 ? (
                      <>
                        {formatNumber(safeNumber(stats.purchasesPerLanguage[item.language]))}
                        <span className="stats-ratio"> ({safeFixed(stats.ratioPurchasesPerLanguage?.[item.language])})</span>
                      </>
                    ) : "-"}
                  </p>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Stats;